import {
  useModalOldContext,
  WSButton,
  WSElement,
  WSFlexBox,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ICreditCard,
  IDefaultPaymentMethod
} from "@wingspanhq/users/dist/lib/interfaces";
import { ExternalBankAccountStatus } from "@wingspanhq/wingspan-api-sdk";
import queryString from "qs";
import React, { useEffect } from "react";
import { queriesExternalBankAccounts } from "@query";
import { selectorExternalBankAccountInstitution } from "@selectors/selectorExternalBankAccountInstitution";
import { selectorExternalBankAccountName } from "@selectors/selectorExternalBankAccountName";
import { useUserId } from "../../../query/hooks/helpers";
import { useClientQuery } from "../../../query/users/queries";
import { useExternalBankAccountReconnect } from "../../../shared/hooks/useExternalBankAccountReconnect";
import { BankCardLogo } from "../../../shared/components/BankCardLogo";
import { CHOOSE_ADD_PAYMENT_METHOD_FLOW_MODAL } from "../PaymentMethodsList/ChooseAddPaymentMethodFlowModal";
import styles from "./NextGenSubscriptionPaymentMethod.module.scss";

type NextGenSubscriptionPaymentMethodOwnProps = {
  paymentMethod: IDefaultPaymentMethod | null;
};
type NextGenSubscriptionPaymentMethodProps =
  NextGenSubscriptionPaymentMethodOwnProps;

export const NextGenSubscriptionPaymentMethod: React.FC<
  NextGenSubscriptionPaymentMethodProps
> = ({ paymentMethod }) => {
  const { openModal } = useModalOldContext();

  const userId = useUserId();
  const clientQuery = useClientQuery(userId);
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const openPlaidReconnect = useExternalBankAccountReconnect();

  useEffect(() => {
    const queries = queryString.parse(window.location.search, {
      ignoreQueryPrefix: true
    });
    if (queries.editPm) {
      const targetEl = document.getElementById(
        "nextGenSubscriptionPaymentMethodSection"
      );
      setTimeout(() => {
        window?.scrollTo({
          behavior: targetEl ? "smooth" : "auto",
          top: targetEl ? targetEl.offsetTop + 700 : 0
        });
      }, 100);
    }
  }, []);

  const getPaymentMethodFromSavedPaymentMethods = ():
    | ICreditCard
    | undefined => {
    const savedPaymentMethods =
      clientQuery.data?.profile.savedPaymentMethods || [];
    const creditCard = savedPaymentMethods.find(
      pm => pm.paymentMethodId === paymentMethod?.paymentMethodId
    );
    return creditCard;
  };

  return (
    <WSElement id="nextGenSubscriptionPaymentMethodSection">
      {paymentMethod ? (
        <WSFlexBox.CenterY
          justify="space-between"
          className={styles.section}
          shimmer={queryExternalBankAccounts.isLoading || clientQuery.isLoading}
        >
          <WSElement>
            <WSText.ParagraphSm color="gray500" mt="2XL">
              Payment
            </WSText.ParagraphSm>
            <WSFlexBox.CenterY mt="XL">
              <WSText.ParagraphSm
                color="gray500"
                shimmer={queryExternalBankAccounts.isLoading}
              >
                {(() => {
                  if (paymentMethod.paymentMethodId) {
                    const card = getPaymentMethodFromSavedPaymentMethods();
                    return (
                      <WSFlexBox.Center>
                        <BankCardLogo
                          className={styles.cardLogo}
                          type="credit"
                          brand={card?.cardBrand}
                        />
                        <WSText.ParagraphSm color="gray500">
                          • • • • {card?.last4}
                        </WSText.ParagraphSm>
                      </WSFlexBox.Center>
                    );
                  }
                  const externalBankAccount =
                    queryExternalBankAccounts.data?.data?.find(
                      eba => eba.id === paymentMethod.accountId
                    );
                  return externalBankAccount ? (
                    <WSFlexBox.Center>
                      <WSText.ParagraphSm mr="XS">
                        {selectorExternalBankAccountInstitution(
                          externalBankAccount
                        )}
                      </WSText.ParagraphSm>
                      <WSText.ParagraphSm mr="XL" inline>
                        {selectorExternalBankAccountName(externalBankAccount)}
                      </WSText.ParagraphSm>
                      {externalBankAccount.status ===
                        ExternalBankAccountStatus.DISCONNECTED && (
                        <WSElement
                          mx="M"
                          onClick={() =>
                            openPlaidReconnect(externalBankAccount)
                          }
                        >
                          <WSText weight="medium" color="red500">
                            Reconnect
                          </WSText>
                        </WSElement>
                      )}
                    </WSFlexBox.Center>
                  ) : null;
                })()}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
          </WSElement>
          <WSButton.Link
            onClick={() =>
              openModal(CHOOSE_ADD_PAYMENT_METHOD_FLOW_MODAL, {
                shouldSetSubscriptionPaymentMethod: true
              })
            }
            name="changePaymentMethod"
          >
            Update
          </WSButton.Link>
        </WSFlexBox.CenterY>
      ) : (
        <WSFlexBox.CenterY justify="space-between" className={styles.section}>
          <WSElement>
            <WSText.ParagraphSm weight="medium" color="gray500" mt="2XL">
              Payment method
            </WSText.ParagraphSm>
            <WSText.ParagraphSm color="gray500" mt="XL">
              No payment method yet.
            </WSText.ParagraphSm>
          </WSElement>
          <WSButton.Link
            onClick={() =>
              openModal(CHOOSE_ADD_PAYMENT_METHOD_FLOW_MODAL, {
                shouldSetSubscriptionPaymentMethod: true
              })
            }
            name="changePaymentMethod"
          >
            Add
          </WSButton.Link>
        </WSFlexBox.CenterY>
      )}
    </WSElement>
  );
};
